import { useState, useRef } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import { useEntities } from '../hooks/useEntities';

const API = `${API_V1}/bulk-upload`;

interface FileItem {
  file: File;
  status: 'QUEUED' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  result?: any;
  error?: string;
}

const DOC_TYPES = [
  { code: '', label: 'Auto-Detect' },
  { code: 'AP_INVOICE', label: 'AP Invoice' },
  { code: 'RECEIPT', label: 'Expense Receipt' },
  { code: 'CONTRACT', label: 'Contract' },
  { code: 'BANK_STATEMENT', label: 'Bank Statement' },
];

export default function DocumentUpload() {
  const entities = useEntities();
  const fileRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [entityCode, setEntityCode] = useState('');
  const [docType, setDocType] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [summary, setSummary] = useState<{ total: number; succeeded: number; failed: number } | null>(null);

  const addFiles = (newFiles: FileList | File[]) => {
    const items: FileItem[] = Array.from(newFiles)
      .filter(f => f.type === 'application/pdf' || f.type.startsWith('image/'))
      .map(f => ({ file: f, status: 'QUEUED' as const }));
    setFiles(prev => [...prev, ...items]);
    setSummary(null);
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => { setFiles([]); setSummary(null); };

  const processAll = async () => {
    if (files.length === 0) return;
    setProcessing(true);
    setSummary(null);

    // Convert all files to base64
    const fileData: { filename: string; base64: string; media_type: string; document_type?: string }[] = [];
    for (const item of files) {
      const b64 = await fileToBase64(item.file);
      fileData.push({
        filename: item.file.name,
        base64: b64,
        media_type: item.file.type || 'application/pdf',
        document_type: docType || undefined,
      });
    }

    // Mark all as processing
    setFiles(prev => prev.map(f => ({ ...f, status: 'PROCESSING' as const })));

    try {
      const res = await authFetch(`${API}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          files: fileData,
          entity_code: entityCode || undefined,
          default_document_type: docType || undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        // Update file statuses from results
        setFiles(prev => prev.map((f, i) => {
          const item = data.items?.[i];
          if (!item) return f;
          return {
            ...f,
            status: item.status === 'SUCCESS' ? 'SUCCESS' : 'FAILED',
            result: item.details,
            error: item.error,
          };
        }));
        setSummary({ total: data.total, succeeded: data.succeeded, failed: data.failed });
      } else {
        const e = await res.json();
        setFiles(prev => prev.map(f => ({ ...f, status: 'FAILED', error: e.detail || 'Upload failed' })));
      }
    } catch (e: any) {
      setFiles(prev => prev.map(f => ({ ...f, status: 'FAILED', error: e.message || 'Network error' })));
    }

    setProcessing(false);
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const statusIcon = (s: string) => s === 'SUCCESS' ? '✓' : s === 'FAILED' ? '✗' : s === 'PROCESSING' ? '⟳' : '○';
  const statusColor = (s: string) => s === 'SUCCESS' ? 'var(--positive)' : s === 'FAILED' ? 'var(--negative)' : s === 'PROCESSING' ? 'var(--accent)' : 'var(--text-secondary)';

  return (
    <div style={{ marginTop: 16 }}>
      {/* Controls row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select value={entityCode} onChange={e => setEntityCode(e.target.value)} style={{ ...inputStyle, minWidth: 220, cursor: 'pointer' }}>
          <option value="">All Entities</option>
          {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} — {e.entity_name}</option>)}
        </select>
        <select value={docType} onChange={e => setDocType(e.target.value)} style={{ ...inputStyle, minWidth: 160, cursor: 'pointer' }}>
          {DOC_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        {files.length > 0 && !processing && (
          <>
            <button onClick={processAll} style={{ padding: '8px 20px', borderRadius: 6, background: 'var(--positive)', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Process {files.filter(f => f.status === 'QUEUED').length} Files
            </button>
            <button onClick={clearAll} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-card)', fontSize: 12, cursor: 'pointer' }}>Clear</button>
          </>
        )}
      </div>

      {/* Drop zone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
        style={{
          border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border-default)'}`,
          borderRadius: 12, padding: files.length > 0 ? '20px 40px' : '50px 40px',
          textAlign: 'center', cursor: 'pointer',
          background: dragOver ? 'var(--accent-subtle, #eef2ff)' : 'var(--bg-card)',
          transition: 'all 0.2s', marginBottom: 16,
        }}
      >
        {processing ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>Processing {files.length} documents...</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Claude Vision is extracting fields from each document</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Drop PDF or image files here — invoices, receipts, contracts, bank statements
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              Supports PDF, PNG, JPG — up to 50 files per batch. Auto-detects document type.
            </div>
          </>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple style={{ display: 'none' }}
          onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {/* Summary */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total', value: summary.total, color: 'var(--text-primary)' },
            { label: 'Succeeded', value: summary.succeeded, color: 'var(--positive)' },
            { label: 'Failed', value: summary.failed, color: summary.failed > 0 ? 'var(--negative)' : 'var(--text-secondary)' },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* File queue */}
      {files.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ borderBottom: '2px solid var(--border-subtle)' }}>
            {['', 'Filename', 'Type', 'Size', 'Status', 'Result', ''].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {files.map((f, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '8px 10px', fontSize: 16, color: statusColor(f.status), fontWeight: 700 }}>{statusIcon(f.status)}</td>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{f.file.name}</td>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)', fontSize: 10 }}>
                  {f.file.type === 'application/pdf' ? 'PDF' : f.file.type.replace('image/', '').toUpperCase()}
                </td>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{(f.file.size / 1024).toFixed(0)} KB</td>
                <td style={{ padding: '8px 10px' }}><StatusBadge status={f.status} /></td>
                <td style={{ padding: '8px 10px', fontSize: 11, color: 'var(--text-secondary)' }}>
                  {f.result ? (
                    <span>
                      {f.result.record_type}: {f.result.invoice_number || f.result.contract_number || f.result.merchant || '-'}
                      {f.result.amount != null && ` — ${formatAmount(f.result.amount)}`}
                    </span>
                  ) : f.error ? (
                    <span style={{ color: 'var(--negative)' }}>{f.error}</span>
                  ) : null}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {f.status === 'QUEUED' && (
                    <button onClick={() => removeFile(i)} style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-default)', background: 'transparent', fontSize: 10, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
